import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiExternalLink,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';

import {
  NonConformityAction,
  getEvidenceUrl,
} from '../../../services/qualityControlService';
import {
  ActionDraft,
  actionDraftSchema,
} from '../../../utils/qualityControlConstants';
import { STATUS_BADGE_STYLE } from './statusBadgeStyle';
import { formatDateTimeBR } from './utils';

/** Linha do plano: rascunho do cadastro ou ação já gravada (com execução). */
export type ActionRow = ActionDraft &
  Partial<
    Pick<
      NonConformityAction,
      | 'id'
      | 'completed_at'
      | 'completed_by_user_name'
      | 'completion_note'
      | 'evidences'
    >
  >;

interface ActionsRepeaterProps {
  title: string;
  addButtonLabel: string;
  value: ActionRow[];
  /**
   * Modo "rascunho local" (criação de NC): recebe o array inteiro já
   * atualizado (adição ou remoção) — usado com useState no componente pai.
   */
  onChange?: (actions: ActionDraft[]) => void;
  /**
   * Modo "ação já persistida" (detalhe de uma NC existente): recebe só o
   * item novo, para o pai chamar a API (`qualityControlService.addAction`)
   * em vez de reescrever o array inteiro. Quando fornecido, a remoção de
   * linhas já salvas fica desabilitada (não há endpoint de remoção de ação
   * ainda — ver docs/CONTROLE_QUALIDADE_MVP.md).
   */
  onAdd?: (action: ActionDraft) => void | Promise<void>;
  isDisabled?: boolean;
  /**
   * Responsável da NC: já vem preenchido no campo "Responsável" de cada nova
   * ação, mas continua editável para uma ação ficar com outra pessoa.
   */
  defaultResponsible?: string;
  /**
   * Ações já gravadas: botão "Concluir ação" nas pendentes (quem abriu, o
   * responsável pela NC ou um master — o pai decide e a API confere).
   */
  canComplete?: boolean;
  onComplete?: (action: ActionRow) => void;
  /**
   * NC já concluída: ação sem execução registrada aparece como "Sem
   * registro" (NCs fechadas antes de existir a execução do plano).
   */
  isClosed?: boolean;
}

const ActionStatus: React.FC<{
  action: ActionRow;
  canComplete: boolean;
  onComplete?: (action: ActionRow) => void;
  isClosed: boolean;
}> = ({ action, canComplete, onComplete, isClosed }) => {
  if (action.completed_at) {
    return (
      <Box>
        <Badge colorScheme="green" {...STATUS_BADGE_STYLE}>
          Concluída
        </Badge>
        <Text fontSize="sm" color="gray.400" mt="1">
          {formatDateTimeBR(action.completed_at)}
          {action.completed_by_user_name
            ? ` · ${action.completed_by_user_name}`
            : ''}
        </Text>
      </Box>
    );
  }

  if (isClosed) {
    return (
      <Badge colorScheme="gray" {...STATUS_BADGE_STYLE}>
        Sem registro
      </Badge>
    );
  }

  return (
    <Flex align="center" gridGap="2" flexWrap="wrap">
      <Badge colorScheme="orange" {...STATUS_BADGE_STYLE}>
        Pendente
      </Badge>
      {canComplete && onComplete && (
        <Button
          size="sm"
          colorScheme="green"
          leftIcon={<FiCheckCircle />}
          onClick={() => onComplete(action)}
        >
          Concluir ação
        </Button>
      )}
    </Flex>
  );
};

/** Observação e evidências de uma ação concluída. */
const ActionExecution: React.FC<{ action: ActionRow }> = ({ action }) => {
  const evidences = action.evidences || [];

  if (!action.completed_at || (!action.completion_note && !evidences.length)) {
    return null;
  }

  return (
    <Box>
      {action.completion_note && (
        <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
          <Text as="span" color="gray.400">
            Execução:{' '}
          </Text>
          {action.completion_note}
        </Text>
      )}
      {evidences.length > 0 && (
        <Flex gridGap="3" flexWrap="wrap" mt="1">
          {evidences.map(evidence => (
            <Link
              key={evidence.id}
              href={getEvidenceUrl(evidence.url)}
              isExternal
              fontSize="sm"
              color="blue.300"
              wordBreak="break-all"
              display="flex"
              alignItems="center"
              gridGap="1"
            >
              <Icon as={FiExternalLink} flexShrink={0} />
              {evidence.original_name}
            </Link>
          ))}
        </Flex>
      )}
    </Box>
  );
};

const emptyDraft: ActionDraft = {
  description: '',
  responsible: '',
  due_date: '',
};

/**
 * Tabela dinâmica "Ação / Responsável / Prazo", controlada fora do Unform —
 * mesmo padrão de LossControlResponsibilitiesField (value/onChange + estado
 * local no pai), já que o Unform Scope não tem precedente no projeto.
 */
export const ActionsRepeater: React.FC<ActionsRepeaterProps> = ({
  title,
  addButtonLabel,
  value,
  onChange,
  onAdd,
  isDisabled = false,
  defaultResponsible = '',
  canComplete = false,
  onComplete,
  isClosed = false,
}) => {
  const [draft, setDraft] = useState<ActionDraft>({
    ...emptyDraft,
    responsible: defaultResponsible,
  });
  const previousDefaultResponsible = useRef(defaultResponsible);

  // Troca do responsável da NC (ex.: escolhido depois no cadastro, ou a NC
  // carregou): acompanha, a menos que o usuário já tenha digitado outro nome.
  useEffect(() => {
    const previous = previousDefaultResponsible.current;
    previousDefaultResponsible.current = defaultResponsible;

    setDraft(current =>
      current.responsible === '' || current.responsible === previous
        ? { ...current, responsible: defaultResponsible }
        : current,
    );
  }, [defaultResponsible]);
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const canRemove = Boolean(onChange) && !onAdd;
  // Situação/execução só existe nas ações já gravadas (detalhe da NC).
  const showStatus = Boolean(onAdd);

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft(current => ({ ...current, description: e.target.value })),
    [],
  );

  const handleResponsibleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft(current => ({ ...current, responsible: e.target.value })),
    [],
  );

  const handleDueDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft(current => ({ ...current, due_date: e.target.value })),
    [],
  );

  const handleAdd = useCallback(async () => {
    try {
      await actionDraftSchema.validate(draft);
    } catch (err) {
      toast({
        title: 'Preencha ação, responsável e prazo antes de adicionar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setAdding(true);
    try {
      if (onAdd) {
        await onAdd(draft);
      } else if (onChange) {
        onChange([...value, draft]);
      }
      setDraft({ ...emptyDraft, responsible: defaultResponsible });
    } finally {
      setAdding(false);
    }
  }, [defaultResponsible, draft, onAdd, onChange, toast, value]);

  const handleRemove = useCallback(
    (index: number) => {
      if (!canRemove || !onChange) return;
      onChange(value.filter((_, i) => i !== index));
    },
    [canRemove, onChange, value],
  );

  return (
    <Box>
      <Heading size="sm" mb={3}>
        {title}
      </Heading>

      {value.length > 0 && isMobile && (
        <Flex direction="column" gridGap="2" mb={3}>
          {value.map((action, index) => (
            <Flex
              // eslint-disable-next-line react/no-array-index-key
              key={`${action.description}-${action.responsible}-${index}`}
              bg="gray.700"
              borderRadius="md"
              p="3"
              justify="space-between"
              align="flex-start"
              gridGap="2"
            >
              <Box minW="0">
                <Text fontSize="sm" wordBreak="break-word">
                  {action.description}
                </Text>
                <Text fontSize="xs" color="gray.400" mt="1">
                  {action.responsible} · Prazo:{' '}
                  {action.due_date.split('-').reverse().join('/')}
                </Text>
                {showStatus && (
                  <Box mt="2">
                    <ActionStatus
                      action={action}
                      canComplete={canComplete}
                      onComplete={onComplete}
                      isClosed={isClosed}
                    />
                    <Box mt="2">
                      <ActionExecution action={action} />
                    </Box>
                  </Box>
                )}
              </Box>
              {!isDisabled && canRemove && (
                <IconButton
                  aria-label="Remover ação"
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleRemove(index)}
                />
              )}
            </Flex>
          ))}
        </Flex>
      )}

      {value.length > 0 && !isMobile && (
        <Table size="sm" variant="simple" mb={3}>
          <Thead>
            <Tr>
              <Th>Ação</Th>
              <Th>Responsável</Th>
              <Th>Prazo</Th>
              {showStatus && <Th>Situação</Th>}
              {!isDisabled && canRemove && <Th w="1" />}
            </Tr>
          </Thead>
          <Tbody>
            {value.map((action, index) => (
              <React.Fragment
                // eslint-disable-next-line react/no-array-index-key
                key={`${action.description}-${action.responsible}-${index}`}
              >
                <Tr>
                  <Td>{action.description}</Td>
                  <Td>{action.responsible}</Td>
                  <Td>{action.due_date.split('-').reverse().join('/')}</Td>
                  {showStatus && (
                    <Td>
                      <ActionStatus
                        action={action}
                        canComplete={canComplete}
                        onComplete={onComplete}
                        isClosed={isClosed}
                      />
                    </Td>
                  )}
                  {!isDisabled && canRemove && (
                    <Td>
                      <IconButton
                        aria-label="Remover ação"
                        icon={<FiTrash2 />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleRemove(index)}
                      />
                    </Td>
                  )}
                </Tr>
                {showStatus && action.completed_at && (
                  <Tr>
                    <Td colSpan={4} pt="0">
                      <ActionExecution action={action} />
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      )}

      {!isDisabled && (
        <Flex gridGap="2" align="flex-end" flexWrap="wrap">
          <Box flex="2" minW={{ base: '100%', md: '200px' }}>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Ação
            </Text>
            <Input
              size="sm"
              value={draft.description}
              onChange={handleDescriptionChange}
              placeholder="Descreva a ação"
            />
          </Box>
          <Box flex="1" minW={{ base: '100%', md: '160px' }}>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Responsável
            </Text>
            <Input
              size="sm"
              value={draft.responsible}
              onChange={handleResponsibleChange}
              placeholder="Responsável"
            />
          </Box>
          <Box minW={{ base: '100%', md: '150px' }}>
            <Text fontSize="xs" color="gray.400" mb={1}>
              Prazo
            </Text>
            <Input
              size="sm"
              type="date"
              value={draft.due_date}
              onChange={handleDueDateChange}
            />
          </Box>
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            onClick={handleAdd}
            isLoading={adding}
            colorScheme="blue"
            variant="outline"
            w={{ base: '100%', md: 'auto' }}
          >
            {addButtonLabel}
          </Button>
        </Flex>
      )}
    </Box>
  );
};
